import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { format } from "date-fns";
import { Calendar, Clock, User, Users, CheckSquare, Search } from "lucide-react";
import AttendanceTracker from "@/components/attendance/AttendanceTracker";

interface TutoringSchedule {
  id: string;
  student_email: string;
  student_name: string | null;
  session_date: string;
  start_time: string;
  end_time: string;
  subject: string;
  topic: string | null;
  status: string;
  list_name?: string;
}

interface StudentList {
  id: string;
  list_name: string;
}

export default function UpcomingSessionsGrid() {
  const { user } = useAuth();
  const [schedules, setSchedules] = useState<TutoringSchedule[]>([]);
  const [filteredSchedules, setFilteredSchedules] = useState<TutoringSchedule[]>([]);
  const [studentLists, setStudentLists] = useState<StudentList[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedList, setSelectedList] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSchedule, setSelectedSchedule] = useState<TutoringSchedule | null>(null);
  const [showAttendance, setShowAttendance] = useState(false);

  useEffect(() => {
    loadData();
  }, [user]);

  useEffect(() => {
    filterSchedules();
  }, [schedules, selectedList, searchTerm]);

  const loadData = async () => {
    if (!user) return;

    try {
      // Load upcoming schedules
      const { data: scheduleData, error } = await supabase
        .from('tutoring_schedules')
        .select('*')
        .eq('instructor_id', user.id)
        .eq('status', 'scheduled')
        .gte('session_date', new Date().toISOString().split('T')[0])
        .order('session_date')
        .order('start_time');

      if (error) throw error;

      // Load student lists to map students to lists
      const { data: listData } = await supabase
        .from('instructor_student_lists')
        .select('id, list_name')
        .eq('instructor_id', user.id);

      if (listData) setStudentLists(listData);

      // Load student list associations
      const { data: emailData } = await supabase
        .from('instructor_student_emails')
        .select(`
          email,
          instructor_student_lists(id, list_name)
        `)
        .eq('instructor_student_lists.instructor_id', user.id);

      // Create email to list mapping
      const emailToList: Record<string, string> = {};
      emailData?.forEach(item => {
        if (item.instructor_student_lists) {
          emailToList[item.email] = item.instructor_student_lists.list_name;
        }
      });

      // Add list names to schedules
      const schedulesWithLists = scheduleData?.map(schedule => ({
        ...schedule,
        list_name: emailToList[schedule.student_email] || 'Individual'
      })) || [];

      setSchedules(schedulesWithLists);
    } catch (error) {
      console.error('Error loading schedules:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterSchedules = () => {
    let filtered = schedules;

    // Filter by student list
    if (selectedList !== 'all') {
      if (selectedList === 'individual') {
        filtered = filtered.filter(s => s.list_name === 'Individual');
      } else {
        filtered = filtered.filter(s => s.list_name === selectedList);
      }
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(s => 
        (s.student_name?.toLowerCase().includes(term)) ||
        s.student_email.toLowerCase().includes(term) ||
        s.subject.toLowerCase().includes(term) ||
        (s.topic?.toLowerCase().includes(term))
      );
    }

    setFilteredSchedules(filtered);
  };

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return 'default';
      case 'completed': return 'secondary';
      case 'cancelled': return 'destructive';
      default: return 'default';
    }
  };

  const openAttendanceTracker = (schedule: TutoringSchedule) => {
    setSelectedSchedule(schedule);
    setShowAttendance(true);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          Upcoming Sessions
        </CardTitle>
        <CardDescription>
          View and manage your upcoming tutoring sessions
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search students, subjects, or topics..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <Select value={selectedList} onValueChange={setSelectedList}>
            <SelectTrigger className="w-full md:w-48">
              <SelectValue placeholder="Filter by list" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Students</SelectItem>
              <SelectItem value="individual">Individual Students</SelectItem>
              {studentLists.map(list => (
                <SelectItem key={list.id} value={list.list_name}>
                  {list.list_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Sessions Grid */}
        {filteredSchedules.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
            {filteredSchedules.map((schedule) => (
              <Card key={schedule.id} className="relative">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <p className="font-medium text-sm truncate">
                          {schedule.student_name || schedule.student_email}
                        </p>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        {schedule.student_email}
                      </p>
                    </div>
                    <Badge variant={getStatusColor(schedule.status)} className="text-xs">
                      {schedule.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="h-3 w-3 text-muted-foreground" />
                      <span>{format(new Date(schedule.session_date), 'MMM dd, yyyy')}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="h-3 w-3 text-muted-foreground" />
                      <span>
                        {formatTime(schedule.start_time)} - {formatTime(schedule.end_time)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Users className="h-3 w-3 text-muted-foreground" />
                      <Badge variant="outline" className="text-xs">
                        {schedule.list_name}
                      </Badge>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      {schedule.subject}
                    </Badge>
                    {schedule.topic && (
                      <p className="text-xs text-muted-foreground mt-1 truncate">
                        Topic: {schedule.topic}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex gap-2 mt-4">
                    <Dialog open={showAttendance && selectedSchedule?.id === schedule.id} onOpenChange={(open) => {
                      setShowAttendance(open);
                      if (!open) setSelectedSchedule(null);
                    }}>
                      <DialogTrigger asChild>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="flex-1 text-xs"
                          onClick={() => openAttendanceTracker(schedule)}
                        >
                          <CheckSquare className="h-3 w-3 mr-1" />
                          Attendance
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                        {selectedSchedule && (
                          <AttendanceTracker
                            scheduleId={selectedSchedule.id}
                            sessionDate={selectedSchedule.session_date}
                            subject={selectedSchedule.subject}
                            onClose={() => {
                              setShowAttendance(false);
                              setSelectedSchedule(null);
                            }}
                          />
                        )}
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              {searchTerm || selectedList !== 'all' 
                ? 'No sessions match your filters' 
                : 'No upcoming sessions scheduled'
              }
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}